"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AbstractItemsJSONModel = void 0;
const lodash_1 = __importDefault(require("lodash"));
class AbstractItemsJSONModel {
    constructor(serializedState, factoryFns) {
        this.items = {};
        this.activeUniqueId = undefined;
        this.itemCounter = 0;
        this.reset = () => {
            this.resetItems();
            this.resetActiveUniqueId();
        };
        this.overwriteFromSerializedState = (serializedState) => {
            if (!lodash_1.default.isNil(serializedState) && lodash_1.default.isPlainObject(serializedState)) {
                this.reset();
                this.setItems(serializedState.items);
                this.setActiveUniqueId(serializedState.activeUniqueId);
            }
        };
        this.serialize = () => {
            const items = lodash_1.default.map(this.getItemsSortedArray(), (item) => {
                return {
                    uniqueId: item.uniqueId,
                    sortingIndex: item.sortingIndex,
                    data: this.factoryFns.serialize(item.data)
                };
            });
            return {
                activeUniqueId: this.activeUniqueId,
                items: items
            };
        };
        ////////////////////////////////////////////////
        // Items
        ////////////////////////////////////////////////
        this.resetItems = () => this.items = {};
        this.getItems = () => this.items;
        this.setItems = (items) => {
            lodash_1.default.each(items, ({ uniqueId, sortingIndex, data: dataSerialized }) => {
                const dataDeserialized = this.factoryFns.deserialize(dataSerialized);
                this._addItemWithDataDeserialized(uniqueId, sortingIndex, dataDeserialized);
            });
            const maximumUniqueId = lodash_1.default.max(lodash_1.default.map(lodash_1.default.keys(this.getItems()), value => lodash_1.default.toNumber(value)));
            if (lodash_1.default.isFinite(maximumUniqueId)) {
                this.itemCounter = maximumUniqueId + 1;
            }
        };
        this.getItemsArray = () => lodash_1.default.values(this.getItems());
        this.getItemsSortedArray = (sortBy) => lodash_1.default.sortBy(this.getItemsArray(), sortBy || ((v) => v.sortingIndex));
        this.enumerateItems = (callback) => {
            lodash_1.default.each(this.getItemsSortedArray(), (item, itemIndex) => {
                const { uniqueId, data: dataDeserialized } = item;
                callback(uniqueId, itemIndex, dataDeserialized);
            });
        };
        this.getItemsCount = () => lodash_1.default.size(this.getItems());
        this.hasItems = () => this.getItemsCount() > 0;
        this.addItem = (dataSerialized, makeActive) => {
            const uniqueId = this.itemCounter++;
            const sortingIndex = this.getItemsCount(); //_.keys(json.state).length
            const dataDeserialized = this.factoryFns.deserialize(dataSerialized);
            const result = this._addItemWithDataDeserialized(uniqueId, sortingIndex, dataDeserialized);
            if (makeActive || this.getItemsCount() === 1) {
                this.setActiveUniqueId(uniqueId);
            }
            return result;
        };
        this.canRemoveItem = (uniqueId) => {
            const itemToRemove = this.getItem(uniqueId);
            if (itemToRemove) {
                const itemsCount = this.getItemsCount();
                return itemsCount > 1;
            }
            return false;
        };
        this.removeItem = (uniqueId) => {
            const itemToRemove = this.getItem(uniqueId);
            if (itemToRemove) {
                const activeUniqueId = this.getActiveUniqueId();
                if (activeUniqueId === uniqueId) {
                    //removing active item
                    const { sortingIndex: sortingIndexOfItemToRemove } = itemToRemove;
                    let newActiveItemUniqueId = undefined;
                    if (sortingIndexOfItemToRemove === 0) {
                        //first item to be removed - next will be active
                        const nexItem = this.getItemBySortingIndex(sortingIndexOfItemToRemove + 1);
                        if (nexItem) {
                            const { uniqueId: nextItemUniqueId } = nexItem;
                            newActiveItemUniqueId = nextItemUniqueId;
                        }
                    }
                    else {
                        //other item to be removed - previous will be active
                        const previousItem = this.getItemBySortingIndex(sortingIndexOfItemToRemove - 1);
                        if (previousItem) {
                            const { uniqueId: previousItemUniqueId } = previousItem;
                            newActiveItemUniqueId = previousItemUniqueId;
                        }
                    }
                    if (!lodash_1.default.isNil(newActiveItemUniqueId) && lodash_1.default.isFinite(newActiveItemUniqueId)) {
                        this.setActiveUniqueId(newActiveItemUniqueId);
                    }
                }
                //remove item
                delete this.items[uniqueId];
                //resort
                lodash_1.default.each(this.getItemsSortedArray(), (item, idx) => {
                    item.sortingIndex = idx;
                });
                return true;
            }
            return false;
        };
        this.moveItem = (uniqueId, toIndex) => {
            const itemsCount = this.getItemsCount();
            if (itemsCount > toIndex) {
                const isMoveToLastPosition = itemsCount - 1 === toIndex;
                let item = this.getItem(uniqueId);
                if (item) {
                    const itemAtThisSortingIndex = this.getItemBySortingIndex(toIndex);
                    if (itemAtThisSortingIndex) {
                        //swap items
                        item.sortingIndex = toIndex;
                        itemAtThisSortingIndex.sortingIndex = isMoveToLastPosition ? toIndex - 0.5 : toIndex + 0.5;
                        //resort
                        lodash_1.default.each(this.getItemsSortedArray(), (item, idx) => {
                            item.sortingIndex = idx;
                        });
                        return true;
                    }
                }
            }
            return false;
        };
        this.getItem = (uniqueId) => {
            if (!lodash_1.default.isNil(uniqueId)) {
                let item = this.items[uniqueId];
                if (item) {
                    return item;
                }
            }
            return undefined;
        };
        this.duplicateItem = (uniqueId) => {
            let item = this.getItem(uniqueId);
            if (item) {
                const { data: dataDeserialized } = item;
                return this.factoryFns.serialize(dataDeserialized);
            }
            return undefined;
        };
        this.getItemBySortingIndex = (sortingIndex) => {
            const item = lodash_1.default.find(this.getItems(), (v) => v.sortingIndex === sortingIndex);
            if (item) {
                return item;
            }
            return undefined;
        };
        this.getData = (uniqueId) => {
            let item = this.getItem(uniqueId);
            if (item) {
                return item.data;
            }
            return undefined;
        };
        this._addItemWithDataDeserialized = (uniqueId, sortingIndex, dataDeserialized) => {
            this.items[uniqueId] = {
                uniqueId: uniqueId,
                sortingIndex: sortingIndex,
                data: dataDeserialized
            };
            return { uniqueId, data: dataDeserialized };
        };
        ////////////////////////////////////////////////
        // ActiveUniqueId
        ////////////////////////////////////////////////
        this.getActiveUniqueId = () => this.activeUniqueId;
        this.setActiveUniqueId = (uniqueId) => {
            let item = this.getItem(uniqueId);
            if (item) {
                this.activeUniqueId = uniqueId;
            }
            else {
            }
        };
        this.resetActiveUniqueId = () => this.activeUniqueId = undefined;
        this.factoryFns = factoryFns;
        if (lodash_1.default.isNil(serializedState) || !lodash_1.default.isPlainObject(serializedState)) {
            serializedState = {};
        }
        this.reset();
        this.overwriteFromSerializedState(serializedState);
    }
    mapItems(callback) {
        return lodash_1.default.map(this.getItemsSortedArray(), (item, itemIndex) => {
            const { uniqueId, data: dataDeserialized } = item;
            return callback(uniqueId, itemIndex, dataDeserialized);
        });
    }
}
exports.AbstractItemsJSONModel = AbstractItemsJSONModel;
